import { motion } from "framer-motion";
import { Cake, Gem, Gift, Heart, Sparkles } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const OCCASIONS = [
    {
        icon: Heart,
        slug: "weddings",
        title: "Weddings",
        desc: "Grand tiered showstoppers with handcrafted sugar flowers and delicate gold details.",
        badge: "Most requested",
    },
    {
        icon: Cake,
        slug: "birthdays",
        title: "Birthdays",
        desc: "Whimsical pastel creations tailored to every theme, age and favourite colour.",
        badge: "Custom themes",
    },
    {
        icon: Sparkles,
        slug: "vintage-cakes",
        title: "Vintage cakes",
        desc: "Dreamy retro piping, scallops and bows — nostalgic elegance in every swirl.",
        badge: "Trending",
    },
    {
        icon: Gem,
        slug: "engagements",
        title: "Engagements",
        desc: "Romantic florals and elegant minimal textures for your once-in-a-lifetime yes.",
        badge: "Romantic",
    },
    {
        icon: Gift,
        slug: "and-more",
        title: "& everything else",
        desc: "Baby showers, graduations, anniversaries and any moment worth a candle.",
        badge: "Bespoke",
    },
];

export default function Occasions() {
    return (
        <section
            id="occasions"
            data-testid="occasions-section"
            className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28"
        >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className="eyebrow">Made for</p>
                    <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-cocoa sm:text-5xl">
                        every occasion
                    </h2>
                </div>
                <p className="max-w-xs text-sm leading-relaxed text-cocoa-soft">
                    Tell us the date and the vibe — we'll design a cake that fits it
                    perfectly.
                </p>
            </div>

            <ul className="mt-10 sm:mt-14">
                {OCCASIONS.map((o, i) => (
                    <motion.li
                        key={o.slug}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55, delay: i * 0.05 }}
                        className="border-t border-blush/40 last:border-b"
                    >
                        <button
                            type="button"
                            data-testid={`occasion-${o.slug}-card`}
                            onClick={() => scrollToSection("#order")}
                            className="group flex w-full items-center gap-4 rounded-2xl py-6 text-left transition-colors hover:bg-blush-soft/50 sm:gap-8 sm:px-4 sm:py-8"
                        >
                            <span className="w-7 shrink-0 font-serif text-sm font-semibold text-gold sm:w-9 sm:text-base">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="flex-1">
                                <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                    <span className="font-serif text-2xl font-semibold text-cocoa sm:text-4xl">
                                        {o.title}
                                    </span>
                                    <span className="rounded-full bg-leaf-soft px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-leaf sm:text-[11px]">
                                        {o.badge}
                                    </span>
                                </span>
                                <span className="mt-2 block max-w-lg text-sm leading-relaxed text-cocoa-soft sm:text-base">
                                    {o.desc}
                                </span>
                            </span>
                            <span className="grid size-10 shrink-0 place-items-center rounded-full border border-blush/60 text-leaf transition-colors duration-300 group-hover:bg-leaf group-hover:text-cream sm:size-12">
                                <o.icon className="size-4 sm:size-5" />
                            </span>
                        </button>
                    </motion.li>
                ))}
            </ul>
        </section>
    );
}
